// Published-packages SOT — the data (D27 leg 1). See `./published-packages.ts`
// for the rationale, the scope boundary, and the consumer contract.

import type { PublishedPackage, VendoredInternalPackage } from './published-packages.types';

/**
 * Every Syncanix package intended for a public registry. Internal-only packages
 * (`@syncanix/ui`, `graphql-schema`, `eslint-config`, `tsconfig`,
 * `docs-mcp-tools`, the auth-adapters `test-utils`) are deliberately absent —
 * they are build-time/server-side and never customer-installed.
 *
 * The CLI is `live` on npm (`syncanix@0.3.0`); the rest
 * are `planned` and flip per-publish per the package-publishing runbook.
 */
export const PUBLISHED_PACKAGES: readonly PublishedPackage[] = [
  // ── npm: the headline CLI ──────────────────────────────────────────────
  {
    id: 'cli',
    label: 'Syncanix CLI',
    workspacePath: 'packages/cli',
    registry: 'npm',
    // The product's first command is `npx syncanix init`; npm resolves the bare
    // package literally named `syncanix`, so the CLI publishes UNSCOPED.
    publishName: 'syncanix',
    installCommand: 'npx syncanix init',
    customerFacing: true,
    status: 'live',
    liveVersion: '0.3.0',
  },
  // ── npm: customer-facing client libraries ──────────────────────────────
  {
    id: 'sdk-node',
    label: 'Syncanix Node SDK',
    workspacePath: 'packages/sdk-node',
    registry: 'npm',
    publishName: '@syncanix/sdk-node',
    installCommand: 'npm install @syncanix/sdk-node',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'web-components',
    label: 'Syncanix Web Components',
    workspacePath: 'packages/web-components',
    registry: 'npm',
    publishName: '@syncanix/web-components',
    installCommand: 'npm install @syncanix/web-components',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'extractor-plugin-api',
    label: 'Syncanix Extractor Plugin API',
    workspacePath: 'packages/extractor-plugin-api',
    registry: 'npm',
    publishName: '@syncanix/extractor-plugin-api',
    installCommand: 'npm install @syncanix/extractor-plugin-api',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'extractor-plugins',
    label: 'Syncanix First-Party Extractor Plugins',
    workspacePath: 'packages/extractor-plugins',
    registry: 'npm',
    publishName: '@syncanix/extractor-plugins',
    installCommand: 'npm install @syncanix/extractor-plugins',
    customerFacing: true,
    status: 'planned',
  },
  // ── npm: per-IdP auth adapters (drop-in for customer apps) ──────────────
  {
    id: 'auth-adapter-auth0',
    label: 'Syncanix Auth0 Adapter',
    workspacePath: 'packages/auth-adapters/auth0',
    registry: 'npm',
    publishName: '@syncanix/auth0',
    installCommand: 'pnpm add @syncanix/auth0',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-clerk',
    label: 'Syncanix Clerk Adapter',
    workspacePath: 'packages/auth-adapters/clerk',
    registry: 'npm',
    publishName: '@syncanix/clerk',
    installCommand: 'pnpm add @syncanix/clerk',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-cognito',
    label: 'Syncanix Cognito Adapter',
    workspacePath: 'packages/auth-adapters/cognito',
    registry: 'npm',
    publishName: '@syncanix/cognito',
    installCommand: 'pnpm add @syncanix/cognito',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-workos',
    label: 'Syncanix WorkOS Adapter',
    workspacePath: 'packages/auth-adapters/workos',
    registry: 'npm',
    publishName: '@syncanix/workos',
    installCommand: 'pnpm add @syncanix/workos',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-oidc',
    label: 'Syncanix OIDC Adapter',
    workspacePath: 'packages/auth-adapters/oidc',
    registry: 'npm',
    publishName: '@syncanix/oidc',
    installCommand: 'pnpm add @syncanix/oidc',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-custom-jwt',
    label: 'Syncanix Custom-JWT Adapter',
    workspacePath: 'packages/auth-adapters/custom-jwt',
    registry: 'npm',
    publishName: '@syncanix/custom-jwt',
    installCommand: 'pnpm add @syncanix/custom-jwt',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-introspection',
    label: 'Syncanix Introspection Adapter',
    workspacePath: 'packages/auth-adapters/introspection',
    registry: 'npm',
    publishName: '@syncanix/introspection',
    installCommand: 'pnpm add @syncanix/introspection',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-bff',
    label: 'Syncanix BFF Adapter',
    workspacePath: 'packages/auth-adapters/bff',
    registry: 'npm',
    publishName: '@syncanix/bff',
    installCommand: 'pnpm add @syncanix/bff',
    customerFacing: true,
    status: 'planned',
  },
  // ── npm: P-06 auth-adapter expansion (seed→Series-B ICP IdPs) ────────────
  {
    id: 'auth-adapter-firebase',
    label: 'Syncanix Firebase Adapter',
    workspacePath: 'packages/auth-adapters/firebase',
    registry: 'npm',
    publishName: '@syncanix/firebase',
    installCommand: 'pnpm add @syncanix/firebase',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-supabase',
    label: 'Syncanix Supabase Adapter',
    workspacePath: 'packages/auth-adapters/supabase',
    registry: 'npm',
    publishName: '@syncanix/supabase',
    installCommand: 'pnpm add @syncanix/supabase',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-keycloak',
    label: 'Syncanix Keycloak Adapter',
    workspacePath: 'packages/auth-adapters/keycloak',
    registry: 'npm',
    publishName: '@syncanix/keycloak',
    installCommand: 'pnpm add @syncanix/keycloak',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'auth-adapter-entra-external-id',
    label: 'Syncanix Entra External ID Adapter',
    workspacePath: 'packages/auth-adapters/entra-external-id',
    registry: 'npm',
    publishName: '@syncanix/entra-external-id',
    installCommand: 'pnpm add @syncanix/entra-external-id',
    customerFacing: true,
    status: 'planned',
  },
  // NOTE: internal runtime deps (@syncanix/{shared,api-witness,auth-adapters-lib})
  // are VENDORED per ADR-0102, never published — see VENDORED_INTERNAL_PACKAGES below.
  // ── non-JS intent-verification SDKs ─────────────────────────────────────
  {
    id: 'sdk-python',
    label: 'Syncanix Python SDK',
    workspacePath: 'packages/sdk-python',
    registry: 'pypi',
    publishName: 'syncanix-sdk',
    installCommand: 'pip install syncanix-sdk',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'sdk-rust',
    label: 'Syncanix Rust SDK',
    workspacePath: 'packages/sdk-rust',
    registry: 'crates',
    publishName: 'syncanix-sdk-rust',
    installCommand: 'cargo add syncanix-sdk-rust',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'sdk-go',
    label: 'Syncanix Go SDK',
    workspacePath: 'packages/sdk-go',
    registry: 'go-modules',
    // Go modules are identified by their VCS path; published via a git tag,
    // not a registry upload (proxy.golang.org + pkg.go.dev serve from the tag).
    publishName: 'github.com/syncanix-ai/syncanix-sdk-go',
    installCommand: 'go get github.com/syncanix-ai/syncanix-sdk-go',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'sdk-ruby',
    label: 'Syncanix Ruby SDK',
    workspacePath: 'packages/sdk-ruby',
    registry: 'rubygems',
    publishName: 'syncanix-sdk-rails',
    installCommand: 'bundle add syncanix-sdk-rails',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'sdk-php',
    label: 'Syncanix PHP SDK',
    workspacePath: 'packages/sdk-php',
    registry: 'packagist',
    publishName: 'syncanix/sdk-php',
    installCommand: 'composer require syncanix/sdk-php',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'sdk-jvm',
    label: 'Syncanix Spring Boot SDK',
    workspacePath: 'packages/sdk-jvm',
    registry: 'maven-central',
    publishName: 'ai.syncanix:syncanix-sdk-spring-boot',
    installCommand: 'implementation "ai.syncanix:syncanix-sdk-spring-boot"',
    customerFacing: true,
    status: 'planned',
  },
  {
    id: 'sdk-dotnet',
    label: 'Syncanix ASP.NET Core SDK',
    workspacePath: 'packages/sdk-dotnet',
    registry: 'nuget',
    publishName: 'Syncanix.Sdk.AspNetCore',
    installCommand: 'dotnet add package Syncanix.Sdk.AspNetCore',
    customerFacing: true,
    status: 'planned',
  },
  // ── VS Code Marketplace ─────────────────────────────────────────────────
  {
    id: 'vscode-extension',
    label: 'Syncanix Catalog (VS Code extension)',
    workspacePath: 'packages/vscode-extension',
    registry: 'vscode-marketplace',
    // Marketplace identity is `<publisher>.<name>`: publisher `syncanix`.
    publishName: 'syncanix.syncanix-catalog',
    installCommand: 'code --install-extension syncanix.syncanix-catalog',
    customerFacing: true,
    status: 'planned',
  },
];

/**
 * The internal `@syncanix/*` packages that are VENDORED (bundled) into every
 * published artifact that uses them, and NEVER published standalone (ADR-0102).
 * The publish pipeline feeds these names to tsup `noExternal`; the dry-run guard
 * asserts none leak into a published package's `dependencies`.
 */
export const VENDORED_INTERNAL_PACKAGES: readonly VendoredInternalPackage[] = [
  { id: 'shared', packageName: '@syncanix/shared', workspacePath: 'packages/shared' },
  {
    id: 'api-witness',
    packageName: '@syncanix/api-witness',
    workspacePath: 'packages/api-witness',
  },
  {
    id: 'auth-adapters-lib',
    packageName: '@syncanix/auth-adapters-lib',
    workspacePath: 'packages/auth-adapters/lib',
  },
];
